import { VISUALIZER_OPTIONS } from './CustomAnalytics/CreateOrEditChart/constants';

export type TAnalyticsRouteParams = {
  pid: string;
  id: string;
};

export type ModuleConfig = {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  labels: string[] | null;
  timeFrame: TimeFrame;
  scope: Scope;
  metrics: Metric[];
  sortMetric: number;
  sortDesc: boolean;
  chartConfig: ChartConfig;
  resultsConfig: ResultsConfig;
  createdAt: EpochTimeStamp;
  updatedAt: EpochTimeStamp;
};

export type ModuleConfigUpdatePayload = Omit<ModuleConfig, 'createdAt' | 'updatedAt'>;

export enum INTERVAL_OPTIONS {
  HOURLY = 'hour',
  DAILY = 'day',
  WEEKLY = 'week',
  MONTHLY = 'month',
  ANNUALLY = 'year',
}

export type ChartConfigState = {
  title?: string;
  description?: string;
  selectedExperiences?: ExperienceSelectorItem[];
  metrics?: {
    title: string;
    entity: string;
    metric: string;
  };
  timeFrame?: {
    start: Date | null;
    end: Date | null;
    period: INTERVAL_OPTIONS | undefined;
    relative: PartialRelativeDateRange;
  };
  visualizationType?: VISUALIZER_OPTIONS | null;
};

type TimeFrame = {
  type: string;
  absoluteStart: EpochTimeStamp;
  absoluteEnd: EpochTimeStamp;
  relativeTimeOffset: number;
  relativeTimeUnit: string;
  relativeTimeIsCurrentIncluded: boolean;
  timeInterval: INTERVAL_OPTIONS;
  timeZone: string;
};

type Scope = {
  components: string;
  interactions: string[];
  aggContext: {
    dataTypeId: string;
    valueKey: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columnConditions: any | null;
    andOrColConds: string;
    isAggCol: boolean;
  };
  journeyIds: string[];
};

type Metric = {
  name: string;
  components: string;
  interactions: string[];
  countDistinct: boolean;
  cumulativeOverTime: boolean;
};

type ChartConfig = {
  type: VISUALIZER_OPTIONS;
  title: string;
  xAxis: string;
  yAxis: string;
  forceOverTime: boolean;
};

type ResultsConfig = {
  aggregateFunction: string;
  maxResults: number;
  sortOrder: string;
  countDistinct: boolean;
  cumulativeOverTime: boolean;
};

type BarGraphDataPoint = {
  journey_id: string;
  name: string;
  value: number;
  x: number;
  y: number;
};

type TableGraphDataPoint = {
  journey_id: string;
  name: string;
  value: number;
};

type LineGraphDataPoint = {
  x: number;
  y: number;
  name: string;
};

export type LineGraphData = LineGraphDataPoint[];
export type BarGraphData = BarGraphDataPoint[];
export type TableGraphData = TableGraphDataPoint[];

export type TBarGraph = { data: BarGraphData; moduleConfig: ModuleConfig };
export type TLineGraph = { data: LineGraphData; moduleConfig: ModuleConfig };

export type ChartData = TBarGraph | TLineGraph;

export type ModuleConfigPayload = Omit<ModuleConfig, 'id'>;

export enum JourneyAnalyticsCollapsibleModes {
  DRAFT = 'draft',
  COMPLETED = 'completed',
}

// TODO: Similar datetime tuple types (DateRange / RelativeDateRange / AdvancedDateRange)
// are redefined in a few places across the codebase — e.g. the legacy
// `src/pages/Analytics/CustomModuleAnalytics/context/CustomModuleAnalyticsContext.ts`
// declares its own `[number, string]` relative tuples inline. These should be
// consolidated into a single shared module so every Analytics surface stays in sync.
export type DateRange<T> = [T | null, T | null];
export type RelativeDateRange = [number, string];
/**
 * Variant of `RelativeDateRange` whose elements may be undefined. Used when the
 * "relative" values are optional — e.g. `ChartConfigState.timeFrame.relative`
 * holds `[undefined, undefined]` for a Custom (absolute) date range, and a real
 * `[offset, unit]` tuple for preset selections.
 */
export type PartialRelativeDateRange = [number | undefined, string | undefined];
export type AdvancedDateRange = DateRange<Date> | [...DateRange<Date>, ...RelativeDateRange];

export type ExperienceSelectorItem = {
  key: string;
  name: string;
  // TODO: These flags (hasEmail, hasSMS) are temporary solutions introduced in PET-5699 to test the display of Email/SMS related metrics in the Recommendations and DIY selectors.
  // The actual implementation should derive the available recommended and DIY metrics dynamically based on the selected experience's available triggers and actions.
  // A spike ticket (PET-5857) exists to analyze and refactor this to remove hardcoded logic.
  hasEmail?: boolean;
  hasSMS?: boolean;
};

export type MetricOption = { key: string; name: string; value: ChartConfigState['metrics'] };

export type RecommendedMetric = {
  icon: JSX.Element;
  metric: { key: string; name: string };
  time: { period: number; interval: string };
  visualization: VISUALIZER_OPTIONS;
};
